import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { day } from '../lib/format';
import { Avatar, Badge, EmptyState, Spinner, Stars } from '../components/ui';
import type { PublicProfile } from '../lib/types';
import { t } from '../lib/i18n';

/** A public profile — what the API publishes, which is never a phone number. */
export default function Profile() {
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!id) return;
    void api
      .get<PublicProfile>(`/users/${id}`)
      .then(setProfile)
      .catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <div className="page">
        <EmptyState title={t("Profil introuvable")} />
      </div>
    );
  }
  if (!profile) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement…")} />
      </div>
    );
  }

  const { user, reviews } = profile;

  return (
    <div className="page page--mid stack stack--loose">
      <div className="card card--pad row row--wrap" style={{ gap: 16 }}>
        <Avatar name={user.fullName} url={user.avatarUrl} size={72} />
        <div className="stack stack--tight" style={{ flex: 1 }}>
          <h1 style={{ fontSize: 24 }}>{user.fullName}</h1>
          <div className="row row--wrap" style={{ gap: 8 }}>
            {user.identityVerified ? <Badge tone="badge--verified">{t("Identité vérifiée")}</Badge> : null}
            {user.isHost ? <Badge>{t("Hôte")}</Badge> : null}
            <Badge>{t("Niveau")} {user.verificationLevel}/3</Badge>
          </div>
          <span className="small muted">
            {t("Membre depuis")} {day(user.memberSince.slice(0, 10), true)}
            {user.city ? ` · ${user.city}` : ''}
            {user.universityName ? ` · ${user.universityName}` : ''}
          </span>
          <div className="row" style={{ gap: 14 }}>
            <Stars rating={user.ratingAverage} count={user.ratingCount} />
            {user.isHost ? (
              <span className="small muted">
                {t('{count} annonce(s) en ligne', { count: user.activeListings })}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {user.bio ? (
        <div className="card card--pad">
          <p style={{ margin: 0 }}>{user.bio}</p>
        </div>
      ) : null}

      <section className="stack">
        <h2>{t("Avis reçus")}</h2>
        {reviews.length === 0 ? (
          <p className="small muted" style={{ margin: 0 }}>
            {t("Aucun avis publié pour le moment.")}
          </p>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="card card--pad stack stack--tight">
              <div className="row" style={{ gap: 10 }}>
                <Avatar name={review.author.fullName} url={review.author.avatarUrl} size={32} />
                <span className="small strong">{review.author.fullName}</span>
                <span className="spacer" />
                <Stars rating={review.rating} />
              </div>
              {review.body ? (
                <p className="small" style={{ margin: 0 }}>
                  {review.body}
                </p>
              ) : null}
              <span className="tiny muted">{day(review.publishedAt?.slice(0, 10) ?? null, true)}</span>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
